// @/hooks/useContentHotspotSectors.js
import { useStaticQuery, graphql } from 'gatsby';
import { useLanguage } from '@/components/providers/LanguageContext';
import { useState, useEffect } from 'react';
import { contentStore } from '@/services/app/content';

/**
 * Hook to fetch HotspotSectors content from Gatsby's GraphQL layer
 * Uses language context to automatically update content when language changes
 *
 * @param {string} [fallbackLocale='en'] - Fallback locale if context not available or locale not found
 * @returns {Object|null} Content for requested locale or null if no content exists at all
 */
export const useContentHotspotSectors = (fallbackLocale = 'en') => {
  // Get current locale from context or use fallback
  const { locale } = useLanguage?.() || { locale: fallbackLocale };

  // Track content for dynamic updates
  const [content, setContent] = useState(null);

  // Execute the GraphQL query
  const data = useStaticQuery(graphql`
    query {
      allContentHotspotSectors {
        nodes {
          id

          businessServicesLabel
          basicMetalsLabel
          computerElectronicAndOpticalEquipmentLabel
          transportationExceptRailLabel
          fabricatedMetalProductsLabel
          constructionLabel
          rubberAndPlasticsProductsLabel
          motorVehiclesAndOtherTransportEquipmentLabel
          machineryAndOtherElectricalEquipmentLabel
          transportationRailLabel
          basicChemicalsLabel
          industrialGasesLabel
          specialityChemicalsLabel

          locale
        }
      }
    }
  `);

  // Update content when locale changes
  useEffect(() => {
    const nodes = data.allContentHotspotSectors?.nodes || [];

    // First try the requested locale
    const localeContent = nodes.find(node => node.locale === locale);

    // Fall back to English if the requested locale isn't available
    const englishContent = locale !== 'en' ? nodes.find(node => node.locale === 'en') : null;

    // Check if we have GraphQL content
    if (localeContent || englishContent) {
      setContent(localeContent || englishContent || null);
    } else {
      // Try to get content from the store as a fallback
      try {
        const storeContent = contentStore.getHotspotSectorsContent(locale);
        if (storeContent) {
          setContent(storeContent);
        } else {
          setContent(null);
        }
      } catch (err) {
        console.error("Error accessing content store for HotspotSectors content:", err);
        setContent(null);
      }
    }
  }, [locale, data.allContentHotspotSectors?.nodes]);

  // Register effect for content change events
  useEffect(() => {
    const handleContentRefresh = (event) => {
      if (event.detail?.locale) {
        // Force refresh content when an external event requests it
        try {
          const storeContent = contentStore.getHotspotSectorsContent(event.detail.locale);
          if (storeContent) {
            setContent(storeContent);
          }
        } catch (err) {
          console.error("Error refreshing HotspotSectors content:", err);
        }
      }
    };

    // Listen for content refresh events
    window.addEventListener('forceContentRefresh', handleContentRefresh);
    window.addEventListener('forceHotspotSectorsRefresh', handleContentRefresh);

    // Clean up listeners
    return () => {
      window.removeEventListener('forceContentRefresh', handleContentRefresh);
      window.removeEventListener('forceHotspotSectorsRefresh', handleContentRefresh);
    };
  }, []);

  return content;
};

/**
 * Fallback version for components that don't have access to the language context
 * @param {string} locale - Desired locale (defaults to 'en')
 * @returns {Object|null} Content for requested locale or null
 */
export const getContentHotspotSectorsForLocale = (locale = 'en') => {
  // First try to get content from the store
  try {
    const storeContent = contentStore.getHotspotSectorsContent(locale);
    if (storeContent) {
      return storeContent;
    }
  } catch (err) {
    console.error("Error accessing content store for HotspotSectors content:", err);
  }

  // Fall back to Gatsby data if store fails
  try {
    if (typeof window !== 'undefined' && window.__GATSBY_INITIAL_DATA__) {
      // Attempt to access GraphQL data from Gatsby
      const staticQueries = window.__GATSBY_INITIAL_DATA__.staticQueryResults || {};

      for (const queryId in staticQueries) {
        const queryResult = staticQueries[queryId];
        const nodes = queryResult?.data?.allContentHotspotSectors?.nodes || [];

        if (nodes.length > 0) {
          // First try the requested locale
          const localeContent = nodes.find(node => node.locale === locale);

          // Fall back to English if the requested locale isn't available
          const englishContent = locale !== 'en' ? nodes.find(node => node.locale === 'en') : null;

          return localeContent || englishContent || null;
        }
      }
    }
  } catch (error) {
    console.error('Error accessing GraphQL content:', error);
  }

  return null;
};
